import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsStorage } from '../../services/localStorage.service';
import './CreatePatient.css';

const CreatePatient = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    patientName: '',
    age: '',
    gender: '',
    contactNumber: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    emergencyContact: '',
    emergencyContactNumber: '',
    bloodGroup: '',
    dateOfBirth: '',
    admitDate: '',
    dischargeDate: '',
    doctorName: '',
    roomNumber: '',
    diagnosis: '',
    totalBill: '',
    paidAmount: '',
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Map form data to storage format
      const patientData = {
        patient_name: formData.patientName,
        patientName: formData.patientName, // Keep both for compatibility
        age: formData.age ? parseInt(formData.age) : null,
        gender: formData.gender || null,
        contact_number: formData.contactNumber,
        contactNumber: formData.contactNumber,
        email: formData.email || null,
        address: formData.address || null,
        city: formData.city || null,
        state: formData.state || null,
        pincode: formData.pincode || null,
        emergency_contact: formData.emergencyContact || null,
        emergencyContact: formData.emergencyContact,
        emergency_contact_number: formData.emergencyContactNumber || null,
        emergencyContactNumber: formData.emergencyContactNumber,
        blood_group: formData.bloodGroup || null,
        bloodGroup: formData.bloodGroup,
        date_of_birth: formData.dateOfBirth || null,
        dateOfBirth: formData.dateOfBirth,
        admit_date: formData.admitDate || null,
        admitDate: formData.admitDate,
        discharge_date: formData.dischargeDate || null,
        dischargeDate: formData.dischargeDate,
        doctor_name: formData.doctorName || null,
        doctorName: formData.doctorName,
        room_number: formData.roomNumber || null,
        roomNumber: formData.roomNumber,
        diagnosis: formData.diagnosis || null,
        total_bill: formData.totalBill ? parseFloat(formData.totalBill) : 0,
        totalBill: formData.totalBill ? parseFloat(formData.totalBill) : 0,
        paid_amount: formData.paidAmount ? parseFloat(formData.paidAmount) : 0,
        paidAmount: formData.paidAmount ? parseFloat(formData.paidAmount) : 0,
        notes: formData.notes || null,
      };

      const newPatient = patientsStorage.create(patientData);
      
      if (newPatient) {
        alert('Patient registered successfully!');
        navigate('/patient-register');
      } else {
        setError('Failed to register patient. Please try again.');
      }
    } catch (err) {
      console.error('Error creating patient:', err);
      setError('Failed to register patient. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/patient-register');
  };

  return (
    <div className="create-patient">
      <div className="create-patient-header">
        <h1 className="page-title">Create New Patient</h1>
        <p className="page-subtitle">Register a new patient in the system</p>
      </div>

      {error && (
        <div style={{ 
          color: '#ef4444', 
          padding: '10px', 
          marginBottom: '15px', 
          backgroundColor: '#fee2e2', 
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}
      <div className="create-patient-card">
        <form onSubmit={handleSubmit} className="create-patient-form">
          <div className="form-section">
            <h2 className="section-title">Personal Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="patientName">Patient Name <span className="required">*</span></label>
                <input
                  type="text"
                  id="patientName"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter patient name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="age">Age</label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter age"
                />
              </div>
              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth</label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="bloodGroup">Blood Group</label>
                <select
                  id="bloodGroup"
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Contact Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactNumber">Contact Number <span className="required">*</span></label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter contact number"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter email address"
                />
              </div>
            </div>

            <div className="form-row full-width">
              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="form-input"
                  rows="2"
                  placeholder="Enter address"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter city"
                />
              </div>
              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter state"
                />
              </div>
              <div className="form-group">
                <label htmlFor="pincode">Pincode</label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter pincode"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Emergency Contact</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="emergencyContact">Emergency Contact Name</label>
                <input
                  type="text"
                  id="emergencyContact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter emergency contact name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="emergencyContactNumber">Emergency Contact Number</label>
                <input
                  type="tel"
                  id="emergencyContactNumber"
                  name="emergencyContactNumber"
                  value={formData.emergencyContactNumber}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter emergency contact number"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Admission Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="admitDate">Admit Date</label>
                <input
                  type="date"
                  id="admitDate"
                  name="admitDate"
                  value={formData.admitDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="dischargeDate">Discharge Date</label>
                <input
                  type="date"
                  id="dischargeDate"
                  name="dischargeDate"
                  value={formData.dischargeDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="roomNumber">Room Number</label>
                <input
                  type="text"
                  id="roomNumber"
                  name="roomNumber"
                  value={formData.roomNumber}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter room number"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="doctorName">Doctor Name</label>
                <input
                  type="text"
                  id="doctorName"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter doctor name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="diagnosis">Diagnosis</label>
                <input
                  type="text"
                  id="diagnosis"
                  name="diagnosis"
                  value={formData.diagnosis}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter diagnosis"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Billing Information</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="totalBill">Total Bill (₹)</label>
                <input
                  type="number"
                  id="totalBill"
                  name="totalBill"
                  value={formData.totalBill}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label htmlFor="paidAmount">Paid Amount (₹)</label>
                <input
                  type="number"
                  id="paidAmount"
                  name="paidAmount"
                  value={formData.paidAmount}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Additional Notes</h2>
            <div className="form-row full-width">
              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-input"
                  rows="4"
                  placeholder="Enter any additional notes or remarks"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleCancel} className="btn btn-secondary" style={{ backgroundColor: 'transparent', color: '#1e293b', border: '1px solid #e2e8f0' }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePatient;

